import { Grid, Switch } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';

import { DeclarationHighValueGroupType, EnvironmentCountries } from '@e-origin/shared';

import { ContainerList, EucdmCode, HideElementFor, MultiLangCodes, MultipleContainers, TextInput } from '@components';
import { useDeclarant, useGoodLocation } from '@hooks/declarant';
import { retrieveCodes } from '@hooks/retrieveCodes';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { useSettings } from '@hooks/useSettings';
import { BeforeSendingFormData } from '../beforeSendingBatchFormIntializer';

interface TransportDetailsProps {
  form: UseFormReturnType<BeforeSendingFormData>;
  disabled: boolean;
  isExport: boolean;
  isB2BTransaction: boolean;
  declarationGroup?: DeclarationHighValueGroupType;
}

export const TransportDetails: React.FC<TransportDetailsProps> = ({ form, disabled, isExport, isB2BTransaction, declarationGroup }) => {
  const { data: declarant } = useDeclarant();
  const { data: { data: goodsLocations } = {} } = useGoodLocation(declarant?._id);
  const { data: settings } = useSettings();
  const codelistSystems = useCodeListSystem({
    group: declarationGroup,
    isExport,
  });

  return (
    <>
      {isB2BTransaction && isExport && (
        <Grid mb="sm">
          <Grid.Col span={4}>
            <EucdmCode path="transportInformation.departureTransportMeans.identificationNumber">
              <TextInput
                data-pw="departure-transport-means-identification-input"
                {...form.getInputProps('transportInformation.departureTransportMeans.identificationNumber')}
                label="Departure transport means identification"
                size="lg"
                disabled={disabled}
              />
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={4}>
            <EucdmCode path="transportInformation.departureTransportMeans.typeOfIdentification">
              <MultiLangCodes
                {...form.getInputProps('transportInformation.departureTransportMeans.typeOfIdentification')}
                label="Departure transport means type"
                code="CL750"
                disabled={disabled}
                size="lg"
                system={codelistSystems}
                data-pw="departure-transport-means-type-input"
              />
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={4}>
            <EucdmCode path="transportInformation.departureTransportMeans.nationality">
              <MultiLangCodes
                {...form.getInputProps('transportInformation.departureTransportMeans.nationality')}
                label="Departure transport means nationality"
                code="CL165"
                disabled={disabled}
                size="lg"
                system={codelistSystems}
                data-pw="departure-transport-means-nationality-input"
              />
            </EucdmCode>
          </Grid.Col>
        </Grid>
      )}

      <Grid mb="sm">
        {isExport && (
          <>
            <Grid.Col span={3}>
              <EucdmCode path="transportInformation.activeBorderTransportMeans.identificationNumber">
                <TextInput
                  data-pw="active-border-transport-means-identification-input"
                  {...form.getInputProps('transportInformation.activeBorderTransportMeans.identificationNumber')}
                  label="Active border transport means ID"
                  size="lg"
                  disabled={disabled}
                />
              </EucdmCode>
            </Grid.Col>
            <Grid.Col span={3}>
              <EucdmCode path="transportInformation.activeBorderTransportMeans.typeOfIdentification">
                <MultiLangCodes
                  {...form.getInputProps('transportInformation.activeBorderTransportMeans.typeOfIdentification')}
                  label="Active border transport means type"
                  code="TypeOfIdentificationofMeansOfTr"
                  disabled={disabled}
                  size="lg"
                  system={codelistSystems}
                  data-pw="active-border-transport-means-type-input"
                />
              </EucdmCode>
            </Grid.Col>
          </>
        )}
        <HideElementFor condition={() => declarationGroup === DeclarationHighValueGroupType.H2}>
          <Grid.Col span={isExport ? 3 : 6}>
            <EucdmCode path="transportInformation.activeBorderTransportMeans.nationality">
              <MultiLangCodes
                {...form.getInputProps('transportInformation.activeBorderTransportMeans.nationality')}
                label="Active border transport means nationality"
                code="CL165"
                disabled={disabled}
                size="lg"
                system={codelistSystems}
                data-pw="active-border-transport-means-nationality-input"
              />
            </EucdmCode>
          </Grid.Col>
        </HideElementFor>
        <HideElementFor condition={() => declarationGroup === DeclarationHighValueGroupType.H2 && settings.country === EnvironmentCountries.BE}>
          <Grid.Col span={isExport ? 3 : 6}>
            <EucdmCode path="transportInformation.modeOfTransportMeansAtTheBorder">
              <MultiLangCodes
                {...form.getInputProps('transportInformation.modeOfTransportMeansAtTheBorder')}
                label="Transport mode at border"
                code="CL018"
                disabled={disabled}
                size="lg"
                system={codelistSystems}
                data-pw="transport-mode-at-border-input"
              />
            </EucdmCode>
          </Grid.Col>
        </HideElementFor>
      </Grid>

      {isExport && (
        <Grid mb="sm">
          <HideElementFor condition={() => [EnvironmentCountries.BE, EnvironmentCountries.NL].includes(settings.country)}>
            <Grid.Col span={12}>
              <TextInput
                label="Loading Place Identity"
                defaultValue={goodsLocations?.find((location) => location.unLocode === form.values.consignment.locationOfGoods.unLocode)?.address?.city}
                size="lg"
                disabled
                data-pw="loading-place-identity-input"
              />
            </Grid.Col>
          </HideElementFor>
        </Grid>
      )}

      <Grid mb="sm">
        {!isExport && (
          <HideElementFor condition={() => declarationGroup === DeclarationHighValueGroupType.H2}>
            <Grid.Col span={4}>
              <EucdmCode path="transportInformation.arrivalTransportMeans.identificationNumber">
                <TextInput
                  data-pw="arrival-transport-means-identification-input"
                  {...form.getInputProps('transportInformation.arrivalTransportMeans.identificationNumber')}
                  label="Arrival transport means ID"
                  disabled={disabled}
                  size="lg"
                />
              </EucdmCode>
            </Grid.Col>
            <Grid.Col span={4}>
              <EucdmCode path="transportInformation.arrivalTransportMeans.typeOfIdentification">
                <MultiLangCodes
                  system={codelistSystems}
                  data-pw="arrival-transport-means-type-input"
                  {...form.getInputProps('transportInformation.arrivalTransportMeans.typeOfIdentification')}
                  label="Arrival Transport means type"
                  code="CL750"
                  disabled={disabled}
                  size="lg"
                />
              </EucdmCode>
            </Grid.Col>
          </HideElementFor>
        )}
        <HideElementFor condition={() => declarationGroup === DeclarationHighValueGroupType.H2}>
          <Grid.Col span={4}>
            <EucdmCode path="transportInformation.inlandModeOfTransport">
              <MultiLangCodes
                {...form.getInputProps('transportInformation.inlandModeOfTransport')}
                label={settings.country === EnvironmentCountries.NL && isExport ? 'Departure transport means mode' : 'Inland transport mode'}
                code="CL018"
                disabled={disabled}
                size="lg"
                system={codelistSystems}
                data-pw="inland-transport-mode-input"
              />
            </EucdmCode>
          </Grid.Col>
        </HideElementFor>
      </Grid>

      <Grid mb="sm">
        <Grid.Col span={6}>
          <ContainerList
            containers={form.values.transportInformation.transportEquipment.containers}
            onChange={(newContainers) => form.setFieldValue('transportInformation.transportEquipment.containers', newContainers)}
            disabled={disabled || !form.values.transportInformation.transportEquipment.header}
            isExport={isExport}
            rightSection={
              <Switch
                {...form.getInputProps('transportInformation.transportEquipment.header')}
                checked={form.values.transportInformation.transportEquipment.header}
                size="xs"
                disabled={disabled}
              />
            }
            data-pw="container-list"
          />
        </Grid.Col>
      </Grid>

      {isExport && (
        <Grid mb="sm">
          <Grid.Col span={12}>
            <EucdmCode path="transportInformation.countryOfRoutingOfConsignment.country">
              <MultipleContainers
                compact
                containers={form.values.transportInformation.countryOfRoutingOfConsignment.map((s) => s.country)}
                onChange={(newList) =>
                  form.setFieldValue(
                    'transportInformation.countryOfRoutingOfConsignment',
                    newList.map((s) => ({ country: s })),
                  )
                }
                editDisabled={disabled}
                label="Routing of Consignment country"
                fetchOptions={(search) => retrieveCodes('CL008', search, declarant?.language, codelistSystems)}
                data-pw="routing-of-consignment-country"
              />
            </EucdmCode>
          </Grid.Col>
        </Grid>
      )}
    </>
  );
};
